// =========================================================
// ASHP Calculator — lead capture + server-side quote build
// Manual calculate only (no auto-calc while typing/changing)
// Internal users can generate a styled PDF quote page
// =========================================================

const APP_PROXY_ENDPOINT = "/apps/heat-calc";
const CRM_SOURCE_TAG = "ashp-kitbuilder-preview";

const ASHP_PUMP_SIZES_KW = [5, 6, 8.5, 11.2, 14];
const INTERNAL_MODE = Boolean(window.ASHP_CALC_CONFIG?.internalMode);

const DEFAULT_ASHP_PROPERTY_TYPE = "new_build";
const ASHP_PROPERTY_TYPES = {
  new_build: { label: "New Build", heatLossWm2: 38 },
  renovation: { label: "Renovation", heatLossWm2: 45 },
};

const ASHP_PRODUCTS = {
  mitsubishiComponents: [
    { key: "flexPipes",  name: "Insulated flexible connection pipes", sku: "ACC-FCP-S28 (277208-IE)", defaultOn: true },
    { key: "fixItFeet",  name: "Fix it Foot 600 kit",                 sku: "Fix it Foot 600 KIT (265072-IE)", defaultOn: true },
    { key: "drainKit",   name: "Drain socket kit",                    sku: "PAC-SG61DS-EH (410358-IE)", defaultOn: true },
    { key: "pipeConn",   name: "28mm pipe connection",                sku: "ACC-MFI-S28 FTC6 (498755-IE)", defaultOn: true },
    { key: "antifreeze", name: "10L Anti-freeze",                     sku: "ACC-AFZ-010A (624039-IE)", defaultOn: true },
  ]
};

let lastASHPKitLines = [];
let lastASHPKitTotals = null;
let lastASHPCalc = null;
let ashpRunId = 0;
let leadCaptured = false;
let lastInternalPdfUrl = "";

const $ = (id) => document.getElementById(id);

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function setStatus(text) {
  const el = $("statusPill");
  if (el) el.textContent = text;
}

function showCartMsg(msg) {
  const el = document.getElementById("cartMsg");
  if (!el) return;
  el.style.display = msg ? "" : "none";
  el.textContent = msg || "";
}

const ASHP_CASCADE_AREA_LIMIT_M2 = 300;

function isCascadeArea(areaM2) {
  return Number(areaM2 || 0) > ASHP_CASCADE_AREA_LIMIT_M2;
}

function getCascadePopup() {
  return document.getElementById("ashpCascadePopup");
}

function showCascadePopup(calc = {}) {
  const popup = getCascadePopup();
  if (!popup) return;

  const areaM2 = Number(calc.areaM2 || 0);
  const occupants = Number(calc.occupants || 0);
  const heatingKW = Number(calc.heatingKW || 0);
  const hotwaterKW = Number(calc.hotwaterKW || 0);
  const pumpKW = Number(calc.pumpKW || 0);

  const summary = document.getElementById("ashpCascadePopupSummary");
  if (summary) {
    summary.innerHTML = `
      <strong>Project summary</strong><br>
      Floor area: ${escapeHtml(areaM2)}m²<br>
      Occupants: ${escapeHtml(occupants)}<br>
      Estimated space heating load: ${escapeHtml(heatingKW.toFixed(1))}kW<br>
      Estimated hot water allowance: ${escapeHtml(hotwaterKW.toFixed(1))}kW<br>
      Estimated total requirement: ${escapeHtml(pumpKW.toFixed(1))}kW
    `;
  }

  const status = document.getElementById("ashpCascadePopupStatus");
  if (status) status.textContent = "";

  const sendBtn = document.getElementById("ashpCascadeSendBtn");
  if (sendBtn) {
    sendBtn.style.display = INTERNAL_MODE ? "none" : "";
    sendBtn.disabled = false;
    sendBtn.textContent = "Send enquiry";
  }

  popup.hidden = false;
  document.body.classList.add("ashp-cascade-popup-open");
}

function hideCascadePopup() {
  const popup = getCascadePopup();
  if (!popup) return;

  popup.hidden = true;
  document.body.classList.remove("ashp-cascade-popup-open");
}

function bindCascadePopup() {
  const popup = getCascadePopup();
  if (!popup) return;

  popup.querySelectorAll("[data-close-ashp-cascade-popup]").forEach((el) => {
    el.addEventListener("click", hideCascadePopup);
  });

  document.getElementById("ashpCascadePopupClose")?.addEventListener("click", hideCascadePopup);
  document.getElementById("ashpCascadeSendBtn")?.addEventListener("click", sendCascadeEnquiry);
}

async function sendCascadeEnquiry() {
  if (INTERNAL_MODE) return;

  const stageEl = document.getElementById("qqProjectStage");
  const nameEl = document.getElementById("qqName");
  const emailEl = document.getElementById("qqEmail");
  const phoneEl = document.getElementById("qqPhone");
  const phoneCountryEl = document.getElementById("qqPhoneCountry");

  const areaM2 = Math.max(0, Number(document.getElementById("areaM2")?.value || 0));
  const occupants = clampNonNegInt(Number(document.getElementById("occupants")?.value || 0));
  const propertyType = getPropertyType();
  const propertyTypeLabel = getPropertyTypeLabel(propertyType);
  const heatLossWm2 = getHeatLossWattsPerM2(propertyType);
  const factor = getHeatLossFactor(propertyType);
  const buildStandardLabel = propertyTypeLabel; // legacy field name kept for existing Zoho/PDF payloads
  const brand = getBrand();
  const cylinderType = getCylinderType();
  const selectedComps = brand === "mitsubishi" ? selectedMitsubishiComponentSkus() : [];
  const calc = calcASHP({ areaM2, heatLossWm2, occupants });

  const projectStage = String(stageEl?.value || "").trim();
  const name = String(nameEl?.value || "").trim();
  const email = String(emailEl?.value || "").trim();
  const phoneCountry = String(phoneCountryEl?.value || "UK").toUpperCase();
  const phone = normaliseUkIePhone(phoneEl?.value || "", phoneCountry);

  const status = document.getElementById("ashpCascadePopupStatus");
  const sendBtn = document.getElementById("ashpCascadeSendBtn");

  if (!projectStage) {
    if (status) status.textContent = "Please select what stage you are at first.";
    stageEl?.focus();
    return;
  }

  if (!name) {
    if (status) status.textContent = "Please enter your name first.";
    nameEl?.focus();
    return;
  }

  if (!isValidEmail(email)) {
    if (status) status.textContent = "Please enter a valid email address first.";
    emailEl?.focus();
    return;
  }

  try {
    if (sendBtn) {
      sendBtn.disabled = true;
      sendBtn.textContent = "Sending…";
    }

    if (status) status.textContent = "Sending enquiry to BPC Heat…";

    const payload = {
      action: "zoho_flow_quote",
      systemType: "ashp",
      name,
      email,
      projectStage,
      customer: {
        name,
        email,
        phone,
        phoneCountry,
        projectStage,
      },
      extra: {
        phone,
        phoneCountry,
        projectStage,
        enquiryType: "ASHP cascade/manual estimate",
        cascadeRequired: true,
      },
      inputs: {
        areaM2,
        occupants,
        propertyType,
        propertyTypeLabel,
        heatLossWm2,
        factor,
        buildStandardLabel,
      },
      options: {
        brand,
        cylinderType,
        mitsubishiComponents: selectedComps,
      },
      results: {
        ...calc,
        cascadeRequired: true,
        areaLimitM2: ASHP_CASCADE_AREA_LIMIT_M2,
      },
      meta: {
        source: "ashp-cascade-enquiry",
        page: location.href,
        systemType: "ashp",
        phoneCountry,
        projectStage,
        cascadeRequired: true,
        manualEstimateRequired: true,
      },
    };

    const out = await callCalcProxy(payload);

    if (!out?.ok) {
      throw new Error(out?.error || "Could not send enquiry.");
    }

    leadCaptured = true;
    updatePrimaryButtonState();
    setStatus("Enquiry sent");

    if (status) {
      status.textContent = "Enquiry sent. BPC Heat will review this as a manual cascade estimate.";
    }

    if (sendBtn) {
      sendBtn.textContent = "Enquiry sent";
    }
  } catch (e) {
    console.error(e);

    if (status) {
      status.textContent = e?.message || "Could not send enquiry. Please contact BPC Heat.";
    }

    if (sendBtn) {
      sendBtn.disabled = false;
      sendBtn.textContent = "Send enquiry";
    }
  }
}

function round1(n) {
  return Math.round(Number(n || 0) * 10) / 10;
}

function clampNonNegInt(n) {
  const x = Number.isFinite(Number(n)) ? Number(n) : 0;
  return Math.max(0, Math.round(x));
}

function pickNextSize(required, options) {
  const sorted = [...options].sort((a, b) => a - b);
  return sorted.find((x) => x >= required) ?? sorted[sorted.length - 1];
}

function getBrand() {
  return (document.getElementById("brand")?.value || "mitsubishi").toLowerCase();
}

function getCylinderType() {
  return (document.getElementById("cylinderType")?.value || "standard").toLowerCase();
}

function normalisePropertyType(value) {
  const raw = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return ASHP_PROPERTY_TYPES[raw] ? raw : DEFAULT_ASHP_PROPERTY_TYPE;
}

function getPropertyType() {
  return normalisePropertyType(document.getElementById("propertyType")?.value);
}

function getPropertyTypeLabel(propertyType = getPropertyType()) {
  const key = normalisePropertyType(propertyType);
  return ASHP_PROPERTY_TYPES[key]?.label || ASHP_PROPERTY_TYPES[DEFAULT_ASHP_PROPERTY_TYPE].label;
}

function getHeatLossWattsPerM2(propertyType = getPropertyType()) {
  const key = normalisePropertyType(propertyType);
  return ASHP_PROPERTY_TYPES[key]?.heatLossWm2 || ASHP_PROPERTY_TYPES[DEFAULT_ASHP_PROPERTY_TYPE].heatLossWm2;
}

function getHeatLossFactor(propertyType = getPropertyType()) {
  return getHeatLossWattsPerM2(propertyType) / 1000;
}

function getInternalMarketSettings() {
  const quoteCountry = String(
    document.getElementById("ashpInternalQuoteCountry")?.value || "GB"
  ).toUpperCase();

  return {
    quoteCountry: quoteCountry === "IE" ? "IE" : "GB",
    quoteCurrency: quoteCountry === "IE" ? "EUR" : "GBP",
  };
}

function selectedMitsubishiComponentSkus() {
  const wrap = document.getElementById("mitCompsList");
  if (!wrap) return [];
  return [...wrap.querySelectorAll("input[type=checkbox][data-sku]")]
    .filter((cb) => cb.checked)
    .map((cb) => cb.getAttribute("data-sku"))
    .filter(Boolean);
}

function formatMoney(amount, currency = "GBP") {
  const n = Number(amount || 0);
  const c = String(currency || "GBP").toUpperCase();
  const locale = c === "EUR" ? "en-IE" : "en-GB";
  const symbol = c === "EUR" ? "€" : "£";

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: c,
    }).format(n);
  } catch {
    return `${symbol}${n.toFixed(2)}`;
  }
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function normaliseUkIePhone(phone, country = "UK") {
  let p = String(phone || "").trim();
  if (!p) return "";

  p = p.replace(/[^\d+]/g, "");

  if (p.startsWith("00")) p = "+" + p.slice(2);
  if (p.startsWith("+44") || p.startsWith("+353")) return p;
  if (p.startsWith("44")) return "+" + p;
  if (p.startsWith("353")) return "+" + p;

  if (p.startsWith("0")) {
    if (String(country).toUpperCase() === "IE") return "+353" + p.slice(1);
    return "+44" + p.slice(1);
  }

  return p;
}

function updatePrimaryButtonState() {
  const btn = $("calcBtn");
  if (!btn) return;
  btn.textContent = leadCaptured ? "Update quote" : "View Quote Now";
}

function calcTotalsFromLines(lines, vatRate = 0.20) {
  const currency = (lines.find((x) => x.currency)?.currency) || "GBP";
  const exVat = (lines || []).reduce((sum, l) => {
    const qty = Number(l.qty || 0);
    const unit = Number(l.price || 0);
    return sum + (unit * qty);
  }, 0);
  const incVat = exVat * (1 + vatRate);
  return { currency, exVat, incVat };
}

function calcASHP({ areaM2, heatLossWm2, factor, occupants }) {
  const effectiveHeatLossWm2 = Math.max(0, Number((heatLossWm2 ?? (Number(factor || 0) * 1000)) || 0));
  const heatingKW = round1((areaM2 * effectiveHeatLossWm2) / 1000);
  const hotwaterKW = round1(occupants * 0.2);
  const pumpKW = round1(heatingKW + hotwaterKW);
  const cylReqL = clampNonNegInt(occupants * 50);
  const pumpSizeKW = pickNextSize(pumpKW, ASHP_PUMP_SIZES_KW);
  return { heatingKW, hotwaterKW, pumpKW, cylReqL, pumpSizeKW, heatLossWm2: effectiveHeatLossWm2 };
}

function renderMitsubishiComponents() {
  const list = document.getElementById("mitCompsList");
  if (!list) return;

  list.innerHTML = ASHP_PRODUCTS.mitsubishiComponents.map((c) => `
    <label class="checkItem">
      <input type="checkbox" data-sku="${escapeHtml(c.sku)}" ${c.defaultOn ? "checked" : ""}>
      <span>
        <span class="checkText">${escapeHtml(c.name)}</span>
        <span class="checkSku">${escapeHtml(c.sku)}</span>
      </span>
    </label>
  `).join("");
}

async function callCalcProxy(payload) {
  const res = await fetch(APP_PROXY_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  const looksJson = ct.includes("application/json") || text.trim().startsWith("{");

  if (!looksJson) {
    throw new Error(
      `Proxy returned non-JSON (content-type: ${ct || "unknown"}). ` +
      `Check APP_PROXY_ENDPOINT. First 200 chars: ${text.trim().slice(0, 200)}`
    );
  }

  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON from proxy. First 200 chars: ${text.trim().slice(0, 200)}`);
  }

  if (!json?.ok || (json?.status && Number(json.status) >= 400)) {
    throw new Error(json?.error || `Server error (status ${json?.status || "?"})`);
  }

  return json;
}

function getBuildStandardLabel() {
  return getPropertyTypeLabel();
}

function renderASHPKit(lines, totals) {
  const tbody = document.getElementById("kitRows");
  if (!tbody) return;

  lastASHPKitLines = Array.isArray(lines) ? lines : [];
  lastASHPKitTotals = totals || null;

  const exEl = document.getElementById("kitExVat");
  const incEl = document.getElementById("kitIncVat");

  if (!lastASHPKitLines.length) {
    tbody.innerHTML = `<tr><td colspan="5" class="muted">No kit lines.</td></tr>`;
    if (exEl) exEl.textContent = "—";
    if (incEl) incEl.textContent = "—";

    const btn = document.getElementById("addToCartBtn");
    if (btn) btn.disabled = true;

    return;
  }

  const anyMissing = lastASHPKitLines.some((x) => !x.variantId);
  const anyOos = lastASHPKitLines.some((x) => x.availableForSale === false);

  tbody.innerHTML = lastASHPKitLines.map((l) => {
    const img = l.image
      ? `<img class="kitImg" src="${escapeHtml(l.image)}" alt="">`
      : `<div class="kitImgPlaceholder" aria-hidden="true"></div>`;

    const qty = Number(l.qty || 0);
    const unit = Number(l.price || 0);
    const currency = String(l.currency || totals?.currency || "GBP");
    const lineTotal = unit * qty;

    return `
      <tr>
        <td class="kitImgCell">${img}</td>
        <td>
          <div class="kitTitle">${escapeHtml(l.title || l.sku || "Item")}</div>
          ${l.note ? `<div class="muted" style="margin-top:4px;font-weight:700;">${escapeHtml(l.note)}</div>` : ``}
          ${(!l.variantId && !l.note) ? `<div class="muted" style="color:#b00020;font-weight:700;margin-top:4px;">SKU not found in Shopify</div>` : ``}
        </td>
        <td class="kitSku" title="${escapeHtml(l.sku || "")}">${escapeHtml(l.sku || "—")}</td>
        <td class="right">${qty}</td>
        <td class="right">${formatMoney(lineTotal, currency)}</td>
      </tr>
    `;
  }).join("");

  let currency = totals?.currency || lastASHPKitLines.find((x) => x.currency)?.currency || "GBP";
  let exText = totals?.exVatText;
  let incText = totals?.incVatText;

  if (!exText || !incText) {
    const fb = calcTotalsFromLines(lastASHPKitLines, 0.20);
    currency = currency || fb.currency;
    exText = formatMoney(fb.exVat, currency);
    incText = formatMoney(fb.incVat, currency);
  }

  if (exEl) exEl.textContent = exText || "—";
  if (incEl) incEl.textContent = incText || "—";

  const btn = document.getElementById("addToCartBtn");
  if (btn) btn.disabled = anyMissing || anyOos;

  if (anyMissing) showCartMsg("One or more items couldn’t be matched by SKU in Shopify. Add to cart disabled.");
  else if (anyOos) showCartMsg("One or more items are out of stock. Please request a quote.");
  else showCartMsg("");
}

async function runCalc() {
  const runId = ++ashpRunId;

  const areaM2 = Math.max(0, Number($("areaM2")?.value || 0));
  const occupants = clampNonNegInt(Number($("occupants")?.value || 0));
  const propertyType = getPropertyType();
  const propertyTypeLabel = getPropertyTypeLabel(propertyType);
  const heatLossWm2 = getHeatLossWattsPerM2(propertyType);
  const factor = getHeatLossFactor(propertyType);
  const buildStandardLabel = propertyTypeLabel; // legacy field name kept for existing Zoho/PDF payloads

  const brand = getBrand();
  const cylinderType = getCylinderType();

  const res = calcASHP({ areaM2, heatLossWm2, occupants });

  $("heatingKW") && ($("heatingKW").textContent = res.heatingKW.toFixed(1));
  $("hotwaterKW") && ($("hotwaterKW").textContent = res.hotwaterKW.toFixed(1));
  $("totalKW") && ($("totalKW").textContent = res.pumpKW.toFixed(1));
  $("pumpSizeKW") && ($("pumpSizeKW").textContent = String(res.pumpSizeKW));
  $("cylReqL") && ($("cylReqL").textContent = String(res.cylReqL));

  if (isCascadeArea(areaM2)) {
  setStatus("Manual estimate required");

  const cylSizeEl = $("cylSizeL");
  if (cylSizeEl) cylSizeEl.textContent = "—";

  const rows = $("kitRows");
  if (rows) {
    rows.innerHTML = `
      <tr>
        <td colspan="5" class="muted">
          Properties over ${ASHP_CASCADE_AREA_LIMIT_M2}m² may require a cascade ASHP system.
          Please send this enquiry to BPC Heat for a manual estimate.
        </td>
      </tr>
    `;
  }

  const exEl = document.getElementById("kitExVat");
  const incEl = document.getElementById("kitIncVat");
  if (exEl) exEl.textContent = "Manual estimate";
  if (incEl) incEl.textContent = "Manual estimate";

  const addBtn = document.getElementById("addToCartBtn");
  if (addBtn) addBtn.disabled = true;

  const pdfBtn = document.getElementById("ashpPrintQuoteBtn");
  if (pdfBtn) pdfBtn.disabled = true;

  lastASHPKitLines = [];
  lastASHPKitTotals = null;
  lastInternalPdfUrl = "";

  showCartMsg("This project is over 300m² and may require a cascade ASHP system. Please enquire for a manual estimate.");

  showCascadePopup({
    areaM2,
    occupants,
    heatingKW: res.heatingKW,
    hotwaterKW: res.hotwaterKW,
    pumpKW: res.pumpKW,
  });

  return;
}

  const compsWrap = document.getElementById("mitCompsWrap");
  if (compsWrap) compsWrap.style.display = (brand === "mitsubishi") ? "" : "none";

  const selectedComps = (brand === "mitsubishi") ? selectedMitsubishiComponentSkus() : [];

  setStatus("Building kit…");

  const tbody = document.getElementById("kitRows");
  if (tbody) tbody.innerHTML = `<tr><td colspan="5" class="muted">Building kit…</td></tr>`;

  const btn = document.getElementById("addToCartBtn");
  if (btn) btn.disabled = true;

  const pdfBtn = document.getElementById("ashpPrintQuoteBtn");
  if (pdfBtn) pdfBtn.disabled = true;

  showCartMsg("");

  let payload;

  if (INTERNAL_MODE) {
  const internalMarket = getInternalMarketSettings();

  payload = {
    action: "build_kit",
    systemType: "ashp",
    customer: {
      name: String(
        document.getElementById("ashpInternalCustomerName")?.value ||
        "Customer"
      ).trim(),
      email: String(
        document.getElementById("ashpInternalCustomerEmail")?.value ||
        ""
      ).trim(),
    },
    internalQuoteNumber: String(
      document.getElementById("ashpInternalQuoteNumber")?.value ||
      ""
    ).trim(),
    inputs: {
      areaM2,
      occupants,
      propertyType,
      propertyTypeLabel,
      heatLossWm2,
      factor,
      buildStandardLabel,
    },
    options: {
      brand,
      cylinderType,
      mitsubishiComponents: selectedComps,
    },
    meta: {
      source: "ashp-internal-calc",
      page: location.href,
      systemType: "ashp",
      internalMode: true,
      quoteType: "internal_pdf_quote",
      internalQuoteNumber: String(
        document.getElementById("ashpInternalQuoteNumber")?.value ||
        ""
      ).trim(),
      quoteCountry: internalMarket.quoteCountry,
      quoteCurrency: internalMarket.quoteCurrency,
      displayCurrency: internalMarket.quoteCurrency,
      presentmentCountry: internalMarket.quoteCountry,
      presentmentCurrency: internalMarket.quoteCurrency,
    },
  };
} else if (!leadCaptured) {
    const stageEl = document.getElementById("qqProjectStage");
    const nameEl = document.getElementById("qqName");
    const emailEl = document.getElementById("qqEmail");
    const phoneEl = document.getElementById("qqPhone");
    const phoneCountryEl = document.getElementById("qqPhoneCountry");

    const projectStage = String(stageEl?.value || "").trim();
    const name = String(nameEl?.value || "").trim();
    const email = String(emailEl?.value || "").trim();
    const phoneCountry = String(phoneCountryEl?.value || "UK").toUpperCase();
    const phone = normaliseUkIePhone(phoneEl?.value || "", phoneCountry);

    if (!projectStage) {
      alert("Please select what stage you are at.");
      stageEl?.focus();
      setStatus("Ready");
      return;
    }

    if (!name) {
      alert("Please enter your name.");
      nameEl?.focus();
      setStatus("Ready");
      return;
    }

    if (!isValidEmail(email)) {
      alert("Please enter a valid email address.");
      emailEl?.focus();
      setStatus("Ready");
      return;
    }

    payload = {
      action: "submit_lead_and_build",
      systemType: "ashp",
      name,
      email,
      projectStage,
      customer: {
        name,
        email,
        phone,
        phoneCountry,
        projectStage,
      },
      extra: {
        phone,
        phoneCountry,
        projectStage,
      },
      inputs: {
        areaM2,
        occupants,
        propertyType,
        propertyTypeLabel,
        heatLossWm2,
        factor,
        buildStandardLabel,
      },
      options: {
        brand,
        cylinderType,
        mitsubishiComponents: selectedComps,
      },
      meta: {
        source: CRM_SOURCE_TAG,
        page: location.href,
        systemType: "ashp",
        phoneCountry,
        projectStage,
      },
    };
  } else {
    payload = {
      action: "build_kit",
      systemType: "ashp",
      inputs: {
        areaM2,
        occupants,
        propertyType,
        propertyTypeLabel,
        heatLossWm2,
        factor,
        buildStandardLabel,
      },
      options: {
        brand,
        cylinderType,
        mitsubishiComponents: selectedComps,
      },
      meta: {
        source: "ashp-update-quote",
        page: location.href,
        systemType: "ashp",
      },
    };
  }

  try {
    const out = await callCalcProxy(payload);
    if (runId !== ashpRunId) return out;

    lastInternalPdfUrl = String(out?.pdfUrl || out?.meta?.pdfUrl || "");

    const pdfBtnAfter = document.getElementById("ashpPrintQuoteBtn");
    if (pdfBtnAfter) {
      pdfBtnAfter.disabled = !(INTERNAL_MODE && lastInternalPdfUrl);
    }

    if (!leadCaptured && payload.action === "submit_lead_and_build" && out?.ok) {
      leadCaptured = true;
      updatePrimaryButtonState();

      const shouldRedirectToThankYou = !INTERNAL_MODE;

      if (shouldRedirectToThankYou && out.redirectUrl) {
        setStatus("Quote ready");
        window.location.href = out.redirectUrl;
        return out;
      }

      if (shouldRedirectToThankYou && out.thankYouPageUrl) {
        setStatus("Quote ready");
        window.location.href = out.thankYouPageUrl;
        return out;
      }
    }

    lastASHPCalc = {
      type: "ashp",
      quoteRef: String(out.quoteRef || out?.meta?.quoteRef || ""),
      inputs: out.inputs || { areaM2, occupants, propertyType, propertyTypeLabel, heatLossWm2, factor, buildStandardLabel },
      options: out.options || { brand, cylinderType, mitsubishiComponents: selectedComps },
      results: out.results || {},
      totals: out?.kit?.totals || {},
    };

    const cylSizeEl = $("cylSizeL");
    if (cylSizeEl) {
      const l = out?.results?.cylinderChosenLitres;
      cylSizeEl.textContent = (brand === "mitsubishi" && cylinderType !== "none" && l) ? String(l) : "—";
    }

    renderASHPKit(out?.kit?.lines || [], out?.kit?.totals || null);

    if (INTERNAL_MODE && lastInternalPdfUrl) {
      setStatus("Calculated");
      showCartMsg("Kit calculated. You can now open the PDF quote.");
    } else {
      setStatus(leadCaptured ? "Quote updated" : "Calculated");
    }

    return out;
  } catch (e) {
    console.error(e);
    if (runId !== ashpRunId) return;

    setStatus("Error");

    if (tbody) {
      tbody.innerHTML = `<tr><td colspan="5" class="muted">${escapeHtml(e?.message || "Server error")}</td></tr>`;
    }

    const exEl = document.getElementById("kitExVat");
    const incEl = document.getElementById("kitIncVat");
    if (exEl) exEl.textContent = "—";
    if (incEl) incEl.textContent = "—";

    showCartMsg(e?.message || "Could not build kit.");
  }
}

async function addKitToCart() {
  const btn = document.getElementById("addToCartBtn");
  if (btn) btn.disabled = true;
  showCartMsg("");

  try {
    if (!lastASHPKitLines.length) throw new Error("No kit lines.");

    const anyMissing = lastASHPKitLines.some((x) => !x.variantId);
    if (anyMissing) throw new Error("Some items could not be matched by SKU. Please check SKUs.");

    const anyOos = lastASHPKitLines.some((x) => x.availableForSale === false);
    if (anyOos) throw new Error("One or more items are out of stock. Please request a quote.");

    const items = lastASHPKitLines.map((x) => ({
      id: Number(x.variantId),
      quantity: Number(x.qty || 0),
    }));

    const addRes = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });

    if (!addRes.ok) {
      const t = await addRes.text().catch(() => "");
      throw new Error(`Add to cart failed: ${t.slice(0, 200)}`);
    }

    const attrs = {
      attributes: {
        quote_source: "instant_quote_ashp",
        quote_system: "ashp",
        quote_ref: String(lastASHPCalc?.quoteRef || ""),
        quote_page: window.location.href,
        quote_customer_name: String(
          document.getElementById("qqName")?.value ||
          window.ASHP_CALC_CONFIG?.customerName ||
          ""
        ).trim(),
        quote_customer_email: String(
          document.getElementById("qqEmail")?.value ||
          window.ASHP_CALC_CONFIG?.customerEmail ||
          ""
        ).trim(),
      },
    };

    const attrRes = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(attrs),
    });

    if (!attrRes.ok) {
      throw new Error("Kit added, but could not stamp cart attributes.");
    }

    window.location.href = "/checkout?instant=1";
  } catch (e) {
    console.error(e);
    showCartMsg(e?.message || "Add to cart failed.");
  } finally {
    if (btn) {
      const anyMissing = lastASHPKitLines.some((x) => !x.variantId);
      const anyOos = lastASHPKitLines.some((x) => x.availableForSale === false);
      btn.disabled = anyMissing || anyOos || !lastASHPKitLines.length;
    }
  }
}

async function copyResults() {
  try {
    const ex = document.getElementById("kitExVat")?.textContent || "—";
    const inc = document.getElementById("kitIncVat")?.textContent || "—";

    const txt = [
      `Property type: ${String(lastASHPCalc?.inputs?.propertyTypeLabel || getPropertyTypeLabel() || "—")}`,
      `Heat loss allowance: ${String(lastASHPCalc?.inputs?.heatLossWm2 || getHeatLossWattsPerM2() || "—")} W/m²`,
      `Space heating load: ${$("heatingKW")?.textContent || "—"} kW`,
      `Hot water allowance: ${$("hotwaterKW")?.textContent || "—"} kW`,
      `Total requirement: ${$("totalKW")?.textContent || "—"} kW`,
      `Recommended pump size: ${$("pumpSizeKW")?.textContent || "—"} kW`,
      `Cylinder litres required: ${$("cylReqL")?.textContent || "—"} L`,
      `Recommended cylinder size: ${$("cylSizeL")?.textContent || "—"} L`,
      `Ex VAT: ${ex}`,
      `Inc VAT: ${inc}`,
      `Quote ref: ${String(lastASHPCalc?.quoteRef || "—")}`,
    ].join("\n");

    await navigator.clipboard.writeText(txt);
    showCartMsg("Copied ✅");
    setTimeout(() => showCartMsg(""), 1500);
  } catch (e) {
    console.error(e);
    showCartMsg("Copy failed.");
  }
}

function resetAll() {
  $("areaM2") && ($("areaM2").value = "");
  $("occupants") && ($("occupants").value = "");
  $("propertyType") && ($("propertyType").value = DEFAULT_ASHP_PROPERTY_TYPE);

  $("brand") && ($("brand").value = "mitsubishi");
  $("cylinderType") && ($("cylinderType").value = "standard");

  $("qqName") && ($("qqName").value = "");
  $("qqEmail") && ($("qqEmail").value = "");
  $("qqPhone") && ($("qqPhone").value = "");
  $("qqPhoneCountry") && ($("qqPhoneCountry").value = "UK");
  $("qqProjectStage") && ($("qqProjectStage").value = "");
  $("ashpInternalCustomerName") && ($("ashpInternalCustomerName").value = "");
  $("ashpInternalCustomerEmail") && ($("ashpInternalCustomerEmail").value = "");
  $("ashpInternalQuoteNumber") && ($("ashpInternalQuoteNumber").value = "");
  $("ashpInternalQuoteCountry") && ($("ashpInternalQuoteCountry").value = "GB");

  $("heatingKW") && ($("heatingKW").textContent = "—");
  $("hotwaterKW") && ($("hotwaterKW").textContent = "—");
  $("totalKW") && ($("totalKW").textContent = "—");
  $("pumpSizeKW") && ($("pumpSizeKW").textContent = "—");
  $("cylReqL") && ($("cylReqL").textContent = "—");
  $("cylSizeL") && ($("cylSizeL").textContent = "—");

  const exEl = document.getElementById("kitExVat");
  const incEl = document.getElementById("kitIncVat");
  if (exEl) exEl.textContent = "—";
  if (incEl) incEl.textContent = "—";

  const rows = $("kitRows");
  if (rows) rows.innerHTML = `<tr><td colspan="5" class="muted">Run a calculation to populate kit lines.</td></tr>`;

  const addBtn = document.getElementById("addToCartBtn");
  if (addBtn) addBtn.disabled = true;

  const pdfBtn = document.getElementById("ashpPrintQuoteBtn");
  if (pdfBtn) pdfBtn.disabled = true;

  lastASHPKitLines = [];
  lastASHPKitTotals = null;
  lastASHPCalc = null;
  lastInternalPdfUrl = "";
  leadCaptured = false;

  updatePrimaryButtonState();
  setStatus("Ready");
  showCartMsg("");
  hideCascadePopup();
}

function bindCylinderSegment() {
  const seg = document.getElementById("cylinderSeg");
  const input = document.getElementById("cylinderType");
  if (!seg || !input) return;

  seg.addEventListener("click", (e) => {
    const btn = e.target.closest(".segBtn");
    if (!btn) return;

    const val = btn.getAttribute("data-cyl");
    if (!val) return;

    input.value = val;

    seg.querySelectorAll(".segBtn").forEach((b) => {
      const active = b === btn;
      b.classList.toggle("is-active", active);
      b.setAttribute("aria-checked", active ? "true" : "false");
    });

    setStatus("Updated");
  });
}

function bindBrandTiles() {
  const wrap = document.querySelector("#ashpCalc .brandTiles");
  if (!wrap) return;

  wrap.addEventListener("click", (e) => {
    const btn = e.target.closest(".brandTile");
    if (!btn) return;

    if (btn.classList.contains("is-disabled") || btn.getAttribute("aria-disabled") === "true") {
      return;
    }

    const brand = btn.getAttribute("data-brand");
    if (!brand) return;

    const brandInput = document.getElementById("brand");
    if (brandInput) brandInput.value = brand;

    wrap.querySelectorAll(".brandTile").forEach((b) => {
      const active = b === btn;
      b.classList.toggle("is-active", active);
      b.setAttribute("aria-checked", active ? "true" : "false");
    });

    const compsWrap = document.getElementById("mitCompsWrap");
    if (compsWrap) compsWrap.style.display = (brand === "mitsubishi") ? "" : "none";

    setStatus("Updated");
  });
}

function bindEvents() {
  $("calcBtn")?.addEventListener("click", runCalc);
  $("resetBtn")?.addEventListener("click", resetAll);
  $("copyBtn")?.addEventListener("click", copyResults);

  ["areaM2", "occupants", "propertyType"].forEach((id) => {
    $(id)?.addEventListener("change", () => setStatus("Updated"));
    $(id)?.addEventListener("input", () => setStatus("Updated"));
  });

  ["qqProjectStage", "qqName", "qqEmail", "qqPhone", "qqPhoneCountry"].forEach((id) => {
    $(id)?.addEventListener("change", () => setStatus("Updated"));
    $(id)?.addEventListener("input", () => setStatus("Updated"));
  });

  document.getElementById("mitCompsList")?.addEventListener("change", () => {
    setStatus("Updated");
  });

  document.getElementById("addToCartBtn")?.addEventListener("click", addKitToCart);
  document.getElementById("ashpPrintQuoteBtn")?.addEventListener("click", openInternalQuotePdf);

  const phoneEl = $("qqPhone");
  const countryEl = $("qqPhoneCountry");

  phoneEl?.addEventListener("input", () => {
    const raw = String(phoneEl.value || "").replace(/\s+/g, "");

    if (raw.startsWith("+44") || raw.startsWith("0044") || raw.startsWith("44")) {
      countryEl.value = "UK";
    } else if (raw.startsWith("+353") || raw.startsWith("00353") || raw.startsWith("353")) {
      countryEl.value = "IE";
    }
  });
}

function openInternalQuotePdf() {
  if (!INTERNAL_MODE) return;

  if (!lastInternalPdfUrl) {
    showCartMsg("Run the quote first, then open the PDF quote.");
    return;
  }

  window.open(lastInternalPdfUrl, "_blank", "noopener,noreferrer");
}

renderMitsubishiComponents();
bindCylinderSegment();
bindBrandTiles();
bindCascadePopup();
bindEvents();

resetAll();